import { Box, Chip, Theme, Typography } from "@mui/material";
import {
  IVacationBidSlot,
  IVacationChangeRequest,
  VacationChangeRequestKind,
  VacationChangeRequestStatus,
  changeRequestKind,
} from "@vmosolutions/oc-interfaces";

import { displaySlotId } from "../helpers";
import { slotDateRange } from "../Awarding/awardingHelpers";
import { REQUEST_STATUS_LABEL, formatInstant } from "./changeRequestHelpers";

export interface IChangeRequestHistoryProps {
  /** One crew member's requests, newest first. */
  requests: IVacationChangeRequest[];
  slotsById: Map<string, IVacationBidSlot>;
  showSlotIdYear: boolean;
  timezoneId: string | undefined;
}

const statusColour = (theme: Theme, status: VacationChangeRequestStatus): string =>
  status === VacationChangeRequestStatus.Pending
    ? theme.palette.warning.main
    : status === VacationChangeRequestStatus.Approved
      ? theme.palette.success.main
      : status === VacationChangeRequestStatus.Denied
        ? theme.palette.error.main
        : theme.custom.forms.labels;

const ChangeRequestHistoryStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  "& .history_row": {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "6px 8px",
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    borderRadius: "3px",
  },
  "& .history_head": { display: "flex", alignItems: "center", gap: "8px" },
  "& .history_status": { height: "20px", fontSize: 11 },
  "& .history_meta": { color: theme.custom.forms.labels },
});

/** A crew member's change requests as they see them, kept here as an audit history for planners. */
export const ChangeRequestHistory = (props: IChangeRequestHistoryProps): JSX.Element | null => {
  const { requests, slotsById, showSlotIdYear, timezoneId } = props;
  if (!requests.length) return null;

  const block = (slotId: string): JSX.Element => {
    const slot = slotsById.get(slotId);
    return (
      <>
        <b>{displaySlotId(slotId, showSlotIdYear)}</b>
        {slot ? ` (${slotDateRange(slot)})` : ""}
      </>
    );
  };

  return (
    <Box sx={ChangeRequestHistoryStyles}>
      {requests.map((request) => (
        <div key={String(request._id)} className="history_row">
          <div className="history_head">
            <Chip
              size="small"
              variant="outlined"
              className="history_status"
              label={REQUEST_STATUS_LABEL[request.status]}
              sx={(theme) => ({
                color: statusColour(theme, request.status),
                borderColor: statusColour(theme, request.status),
              })}
            />
            <Typography variant="body2">
              {changeRequestKind(request) === VacationChangeRequestKind.Swap ? (
                <>
                  Give up {block(request.releaseSlotId)} for {block(request.takeSlotId)}
                </>
              ) : changeRequestKind(request) === VacationChangeRequestKind.PickUp ? (
                <>Pick up {block(request.takeSlotId)}</>
              ) : (
                <>Place leftover days</>
              )}
              {request.leftoverPlacement && (
                <>
                  {request.takeSlotId ? ", leftover days " : " "}
                  {request.leftoverPlacement.side} {block(request.leftoverPlacement.slotId)}
                </>
              )}
            </Typography>
          </div>
          <Typography variant="caption" className="history_meta">
            {`Asked ${formatInstant(request.submittedAt, timezoneId)}`}
            {request.resolvedAt
              ? ` · ${REQUEST_STATUS_LABEL[request.status]} ${formatInstant(request.resolvedAt, timezoneId)}`
              : ""}
          </Typography>
          {!!request.note && (
            <Typography variant="caption" sx={{ fontStyle: "italic" }}>{`Crew note: ${request.note}`}</Typography>
          )}
          {!!request.denyReason && (
            <Typography variant="caption">{`Reason given to crew: ${request.denyReason}`}</Typography>
          )}
        </div>
      ))}
    </Box>
  );
};
